import React, {useState, useEffect, useCallback, useMemo} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./PatientEnrollment.module.css";
import ManagementDropdownCard from "../../components/ManagementDropdownCard";
import ManagementStyles from '../ManagementPages.module.css';
import { setListValue } from "./EnrollmentListSlicer";
import useAxios from '../../services/AxiosConfig';
import { setModalitiesState } from "./PatientModalitiesSlice";
import { formatDateString } from '../../utils/dateUtils';
import Select from 'react-select';
import { customStyles } from '../../components/AccountManagement/CustomStyles';
import { setEnrollmentState, setEnrollmentFormValue } from "./PatientEnrollmentSlice";

const ViewProgram = () => {

    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userId = useSelector((state)=>state.userInfo.id);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const data = useSelector((state)=>state.enrollPatientProgram);
    const deviceList = useSelector((state)=>state.enrollPatientProgram.devices);
    const questionnaireList = useSelector((state)=>state.enrollPatientProgram.questionnaires);
    const contentList = useSelector((state)=>state.enrollPatientProgram.contents);
    const availableEnrollmentsRaw = useSelector((state)=>state.enrollPatientProgram.availableEnrollments);
    const availableEnrollments = useMemo(() => availableEnrollmentsRaw || [], [availableEnrollmentsRaw]);
    const [allModalities, setAllModalities] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
    const [selectedContent, setSelectedContent] = useState([]);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);
    const [isLoadingEnrollment, setIsLoadingEnrollment] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [dropdownEnrollments, setDropdownEnrollments] = useState([]);
    const { notifications, modalityFrequencies } = useSelector((state)=>state.patientEnrollmentModalities);
    const hubs = useSelector(state => state.enrollPatientProgram.hubs);
    const hubSerialNumber = (hubs?.length > 0 && hubs[0]?.serialNumber) ? hubs[0].serialNumber : "";
    const allDevices = useSelector((state)=>state.enrollmentLists.devices);
    const programModalities = useSelector((state)=>state.patientEnrollmentModalities.modalities);
    const errorMessages = {
        program:{patientShowing:false,programShowing:false,durationShowing:false,groupShowing:false,},
        device:{showing:false, modalityShowing: false,modalityErrors: [],deviceIdErrors: [], perinModalityErrors: []},
        rules: {repeatNotificationShowing: false,repeatModalityFrequencyShowing: false,},
        questionnaire:{showing:false},
        content:{contentShowing:false,programShowing:false,},
    };

    const savedModalityValuesRaw = useSelector((state) => state.savedPatientModalities.patientRecentModalities[data.patient.id]);
    const savedModalityValues = useMemo(() => savedModalityValuesRaw ?? {}, [savedModalityValuesRaw]);

    const initModalities = useCallback(async (devices, enrollmentDevices = deviceList) => {
        const modalitiesList = [];
        const seenModalityIds = new Set();
        enrollmentDevices.forEach((device)=>{
            let foundDevice = devices.find(item => item.deviceId === device.deviceId)
            if (foundDevice) {
                foundDevice.modalities.forEach((mod)=>{
                    if (!seenModalityIds.has(mod.modalityId)) {
                        modalitiesList.push({id: mod.modalityId, name:mod.modalityName})
                        seenModalityIds.add(mod.modalityId);
                    }
                })
            }
        })
        setAllModalities(modalitiesList);
    }, [deviceList]);

    const initSelectedDevices = useCallback(async (devices, enrollmentDevices = deviceList) => {
        // check to see if content already exists in the state,
        const tempDevices = [];
        const seenDeviceIds = new Set();
        enrollmentDevices.forEach((item)=> {
            if (seenDeviceIds.has(item.deviceId)) {
                return; // Skip duplicate devices
            }
            let exisitingDeviceIndex = devices.findIndex(device => device.deviceId === item.deviceId);
            if (exisitingDeviceIndex !== -1){
                // if addProgram.content has a match in the content list, add to selectedContent
                tempDevices.push(devices[exisitingDeviceIndex]);
            } else {
                tempDevices.push({
                    id: item.deviceId, 
                    name: item.deviceName
                })
            }
            seenDeviceIds.add(item.deviceId);
        })
        setSelectedDevices(tempDevices);
    }, [deviceList]);

    const initSelectedQuestionnaires = useCallback(async (questionnaires, enrollmentQuestionnaires = questionnaireList) => {
        // check to see if questionnaire already exists in the state,
        const tempQs = [];
        const seenQuestionnaireIds = new Set();
        enrollmentQuestionnaires.forEach((item)=> {
            let existingQIndex = questionnaires.findIndex(q => q.questionnaireId === item.questionnaireId);
            if (existingQIndex !== -1 && !seenQuestionnaireIds.has(item.questionnaireId)){
                // if addProgram.questionnaire has a match in the questionnaire list, add to selectedQuestionnaires
                tempQs.push(questionnaires[existingQIndex]);
                seenQuestionnaireIds.add(item.questionnaireId);
            } else {
                return;
            }
        })
        setSelectedQuestionnaires(tempQs);
    }, [questionnaireList]);

    const initSelectedContent = useCallback(async (content, enrollmentContent = contentList) => {
        // check to see if content already exists in the state,
        const tempContent = [];
        const seenContentIds = new Set();
        enrollmentContent.forEach((item)=> {
            let existingContentIndex = content.findIndex(content => content.id === item.contentId);
            if (existingContentIndex !== -1 && !seenContentIds.has(item.contentId)){
                // if addProgram.content has a match in the content list, add to selectedContent
                tempContent.push(content[existingContentIndex]);
                seenContentIds.add(item.contentId);
            } else {
                return;
            }
        });
        setSelectedContent(tempContent);
    }, [contentList]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    contentResponse,
                    questionnaireResponse,
                    deviceResponse
                  ] = await Promise.all([
                    httpService.get(`content/list/${userId}`),
                    httpService.get(`questionnaire/list/${userId}`),
                    httpService.get(`device/group/${userId}`)
                  ]);
                  dispatch(setListValue({key:"content", value:contentResponse.data.data}));
                  dispatch(setListValue({key:"questionnaires", value:questionnaireResponse.data.data}));
                  await initSelectedQuestionnaires(questionnaireResponse.data.data);
                  await initSelectedContent(contentResponse.data.data);
                  const deviceData = deviceResponse.data.data;
                    let tempDevices = [];
                    let uniqueDevices = new Set();
                    if (deviceData.length >= 1) {
                        deviceData.forEach((device)=>{
                            if (!uniqueDevices.has(device.deviceId)) {
                                tempDevices.push(device);
                                uniqueDevices.add(device.deviceId);
                            }
                        })
                    }
                    dispatch(setListValue({key:"devices", value:tempDevices}));
                    // Use originalDevices (full device-modality list with all rules) so all notifications show; deviceList is deduplicated and only has one modality's rules per device
                    const programDevicesForRules = (data.originalDevices && data.originalDevices.length > 0) ? data.originalDevices : deviceList;
                    dispatch(setModalitiesState({programDevices: programDevicesForRules, allDevices:tempDevices, modalityValues:savedModalityValues}));
                    await initModalities(tempDevices)
                    await initSelectedDevices(tempDevices)
            } catch (error) {
                console.error("Failed to fetch data:", error);
            }
        }
        fetchData();
      }, [dispatch, httpService, userId, deviceList, data.originalDevices, savedModalityValues, initModalities, initSelectedDevices, initSelectedQuestionnaires, initSelectedContent]);

    // Initialize selected enrollment when component mounts or availableEnrollments change
    useEffect(() => {
        if (availableEnrollments.length > 0) {
            // Store enrollments in local state to prevent dropdown from disappearing
            setDropdownEnrollments(availableEnrollments);
            
            if (!selectedEnrollment) {
                // Set the first enrollment as default
                if (data.enrollmentId) {
                    const defaultEnrollment = availableEnrollments.find((enrollment) => enrollment.enrollmentId === data.enrollmentId);
                    setSelectedEnrollment(defaultEnrollment);
                } else {
                    const defaultEnrollment = availableEnrollments[0];
                    setSelectedEnrollment(defaultEnrollment);
                }
            }
            // Mark initialization as complete
            setIsInitializing(false);
        }
    }, [availableEnrollments, selectedEnrollment]);

    // Handle page reload case - redirect if no enrollment data is available
    useEffect(() => {
        const timer = setTimeout(() => {
            if (availableEnrollments.length === 0 && !data.enrollmentId && !isInitializing) {
                // If no enrollment data is available (e.g., page reload), redirect to enrollment list
                navigate('/patient-enrollment');
            }
        }, 1000); // Give 1 second for data to load

        return () => clearTimeout(timer);
    }, [availableEnrollments.length, data.enrollmentId, isInitializing, navigate]);

    const initTeamNames = () => {
        let retString = "";
        data.enrollment.groups.forEach((team)=> {
            retString += `${team.teamName}, `;
        })
        return retString.slice(0, -2);
    };

    const teamNames = initTeamNames();

    const initGroupNames = () => {
        const groupNames = Array.from(new Set(data.enrollment.groups.map((group) => group.groupName)));
        let retString = "";
        groupNames.forEach((group)=> {
            retString += `${group}, `;
        })
        return retString.slice(0, -2);
    };

    const groupNames = initGroupNames();

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return formatDateString(new Date(dateStr).toISOString(), userDateFormat).replace(/\//g, '-');
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateStr;
        }
    };

    // Create dropdown options for available enrollments
    const enrollmentOptions = useMemo(() => {
        return dropdownEnrollments.map((enrollment) => ({
            value: enrollment.enrollmentId,
            label: `${enrollment.name} (${enrollment.enrollment.dischargeDate ? 'Discharged' : 'Active'})`,
            enrollment: enrollment
        }));
    }, [dropdownEnrollments]);

    // Handle enrollment selection change
    const handleEnrollmentChange = (selectedOption) => {
        if (selectedOption && selectedOption.enrollment) {
            const newEnrollment = selectedOption.enrollment;
            setSelectedEnrollment(newEnrollment);
            setIsLoadingEnrollment(true);
            
            // Update Redux state with new enrollment data
            const groups = newEnrollment.enrollment.groups;
            const organizationId = newEnrollment.hasOwnProperty("organizationId") ? newEnrollment.organizationId : false;
            const newGroups = groups.map((group) => {
                return organizationId ? {...group, organizationId: organizationId} : group
            });
            
            dispatch(setEnrollmentFormValue({key:"groups", value:newGroups}));
            dispatch(setEnrollmentState(newEnrollment));
            
            // Clear current data to prevent rendering with stale data
            setSelectedDevices([]);
            setSelectedQuestionnaires([]);
            setSelectedContent([]);
            setAllModalities([]);
            
            // Trigger data refresh for the new enrollment
            fetchEnrollmentData(newEnrollment);
        }
    };

    // Fetch data for a specific enrollment
    const fetchEnrollmentData = async (enrollmentData) => {
        try {
            const [
                contentResponse,
                questionnaireResponse,
                deviceResponse
            ] = await Promise.all([
                httpService.get(`content/list/${userId}`),
                httpService.get(`questionnaire/list/${userId}`),
                httpService.get(`device/group/${userId}`)
            ]);
            
            dispatch(setListValue({key:"content", value:contentResponse.data.data}));
            dispatch(setListValue({key:"questionnaires", value:questionnaireResponse.data.data}));
            
            const deviceData = deviceResponse.data.data;
            let tempDevices = [];
            let uniqueDevices = new Set();
            if (deviceData.length >= 1) {
                deviceData.forEach((device)=>{
                    if (!uniqueDevices.has(device.deviceId)) {
                        tempDevices.push(device);
                        uniqueDevices.add(device.deviceId);
                    }
                })
            }
            dispatch(setListValue({key:"devices", value:tempDevices}));
            dispatch(setModalitiesState({programDevices: enrollmentData.devices, allDevices:tempDevices, modalityValues:savedModalityValues}));
            
            await initModalities(tempDevices, enrollmentData.devices);
            await initSelectedDevices(tempDevices, enrollmentData.devices);
            await initSelectedQuestionnaires(questionnaireResponse.data.data, enrollmentData.questionnaires);
            await initSelectedContent(contentResponse.data.data, enrollmentData.contents);
            
            // Clear loading state when data is loaded
            setIsLoadingEnrollment(false);
        } catch (error) {
            console.error("Failed to fetch enrollment data:", error);
            setIsLoadingEnrollment(false);
        }
    };

    // Show loading message if still initializing
    if (isInitializing && availableEnrollments.length === 0) {
        return (
            <div className={ManagementStyles.MainCard}>
                <div className={ManagementStyles.HideScrollContainer}>
                    <div className={styles.viewProgramContent}>
                        <div style={{textAlign: "center", padding: "50px"}}>
                            <div>Loading enrollment data...</div>
                        </div>
                    </div>
                </div>
            </div>
        );

    }

    return (
        <div className={ManagementStyles.MainCard}>
            <div className={ManagementStyles.HideScrollContainer}>
                <div className={styles.viewProgramContent}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
                        <span style={{color:"4F4F4F"}} className="headline-6">View Program</span>
                        {(() => {
                            const shouldShow = dropdownEnrollments.length > 1 && !isInitializing;
                            return shouldShow;
                        })() && (
                            <div style={{width: "300px"}}>
                                 <Select
                                     key={`enrollment-select-${selectedEnrollment?.enrollmentId || 'default'}`}
                                     options={enrollmentOptions}
                                     styles={customStyles.addressInput}
                                     className="dropdown-input"
                                     placeholder='Select an enrollment'
                                     isSearchable={true}
                                     value={enrollmentOptions.find(option => option.value === selectedEnrollment?.enrollmentId)}
                                     onChange={handleEnrollmentChange}
                                 />
                            </div>
                        )}
                    </div>
                    <div>
                        <span className="subtitle-2">Patient</span>
                        <div className="body-2">{`${data.patient.firstName} ${data.patient.lastName}`}</div>
                    </div>
                    <hr></hr>
                    <span className="subtitle-2">Program Information</span>
                    <div>{`Program Name: ${data.name}`}</div>
                    <div>{`Group: ${groupNames}`}</div>
                    <div>{`Team: ${teamNames}`}</div>
                    <div>{`Notes: ${data.description}`}</div>
                    <div>{`Duration: ${data.duration} days`}</div>
                    <div>{`Admit Date: ${formatDate(data.enrollment?.startDate)}`}</div>
                    <div>{`Expected Discharge Date: ${formatDate(data.enrollment?.endDate)}`}</div>
                    {data.enrollment?.dischargeDate && (
                        <div>{`Actual Discharge Date: ${formatDate(data.enrollment.dischargeDate)}`}</div>
                    )}
                    <hr></hr>
                    <span className="subtitle-2">Device Setup</span>
                    {hubSerialNumber !== "" && 
                        <>
                        <span>Hub</span>
                        <div>{`Serial Number:: ${hubSerialNumber}`}</div>
                        </>
                    }
                    {isLoadingEnrollment ? (
                        <div>Loading enrollment data...</div>
                    ) : (
                        selectedDevices.length > 0 &&
                        <>
                            <div className={styles.viewProgramContentCards}>
                            {selectedDevices.map((device, index) => (
                                <div key={`device-${selectedEnrollment?.enrollmentId || 'default'}-${device.deviceId || device.id || index}`}>
                                    <ManagementDropdownCard 
                                        open={index === 0}
                                        cardInfo={{title:device.model, id:device.deviceId, type:"Device", modalities:device.modalities}} 
                                        dataList={deviceList}
                                        patientEnrollment={true}
                                        viewOnly={true}
                                        errorShowing={errorMessages.device}
                                        modalityList={programModalities}
                                        allDevices={allDevices}
                                    />
                                </div>
                            ))}
                            </div>
                            <div>{`Notes: ${data.deviceNotes}`}</div>
                        </>
                    )}
                    <hr></hr>
                    <span className="subtitle-2">Enrollment Rules</span>
                    {notifications.length > 0 &&
                        <>
                            <span>Notification</span>
                            {notifications.map((not) => {
                                const modalityName = allModalities.find((mod)=>mod.id === not.modalityId)?.name;
                                return (
                                    <span key={not.ruleId} style={{paddingLeft: "10px"}}>
                                    {` - Notify me if ${modalityName} is outside the threshold ${not.thresholdCount} times in ${not.timeFrequency} ${not.timeInterval}`}
                                    </span>
                                )
                            })}
                        </>
                    }
                    {modalityFrequencies.length > 0 &&
                        <>
                            <span>Modality Frequency</span>
                            {modalityFrequencies.map((mod) => {
                                const modalityName = allModalities.find((m)=>m.id === mod.modalityId)?.name;
                                return (
                                    <span key={mod.ruleId} style={{paddingLeft: "10px"}}>
                                    {` - Notify me if ${modalityName} is ${mod.thresholdType} the threshold ${mod.thresholdCount} times in ${mod.timeFrequency} ${mod.timeInterval} increase frequency for ${mod.frequencyMultiplier}x for ${mod.frequencyDurationValue} hour`}
                                    </span>
                                )
                            })}
                        </>
                    }
                    <hr></hr>
                    <span className="subtitle-2">Questionnaire Setup</span>
                    {isLoadingEnrollment ? (
                        <div>Loading questionnaire data...</div>
                    ) : (
                        selectedQuestionnaires.length > 0 && 
                        <>
                            <div className={styles.viewProgramContentCards}>
                                {selectedQuestionnaires.map((q, index) => (
                                    <div key={`questionnaire-${selectedEnrollment?.enrollmentId || 'default'}-${q.questionnaireId}-${index}`}>
                                        <ManagementDropdownCard 
                                            open={index!==0 ? false : true} 
                                            cardInfo={{title:q.name, id:q.questionnaireId, type:"Questionnaire"}}
                                            dataList={questionnaireList}
                                            viewOnly={true}
                                            errorShowing={errorMessages.questionnaire}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div>{`Notes: ${data.questionnaireNotes}`}</div>
                        </>
                    )}
                    <hr></hr>
                    <span className="subtitle-2">Content Setup</span>
                    {isLoadingEnrollment ? (
                        <div>Loading content data...</div>
                    ) : (
                        selectedContent.length > 0 && 
                        <>
                            <div className={styles.viewProgramContentCards}>
                                {selectedContent.map((content, index) => (
                                    <div key={`content-${selectedEnrollment?.enrollmentId || 'default'}-${content.id}-${index}`}>
                                        <ManagementDropdownCard 
                                            open={index!==0 ? false : true} 
                                            cardInfo={{title:content.name, id:content.id, type:"Content"}}
                                            dataList={contentList}
                                            viewOnly={true}
                                            errorShowing={errorMessages.content}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div>{`Notes: ${data.contentNotes}`}</div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewProgram;
